/**
 * ATA derivation and token account balance parsing utilities.
 *
 * Derives Associated Token Account addresses locally (pure math, no RPC)
 * and provides a shared parser for the SPL Token account data layout.
 */

import { TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { PublicKey } from '@solana/web3.js';
import { getUsdcMint } from '@/config/solana';

/**
 * Seeds for the Associated Token Account PDA.
 * Layout: [walletAddress, tokenProgramId, mintAddress]
 * Program: ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL
 */
const ATA_PROGRAM_ID: PublicKey = new PublicKey('ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL');

/** Derive the canonical USDC ATA for a wallet (SPL Token program). */
export function deriveUsdcAta(walletAddress: string): PublicKey {
  const wallet = new PublicKey(walletAddress);
  const mint = new PublicKey(getUsdcMint());
  const [ata] = PublicKey.findProgramAddressSync(
    [wallet.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), mint.toBuffer()],
    ATA_PROGRAM_ID,
  );
  return ata;
}

/**
 * Derive the canonical outcome token ATA for a wallet. Outcome mints are
 * created under the classic SPL Token program (`init_outcome.rs` declares
 * `token_program: Program<Token>` and mints via `anchor_spl::token::MintTo`),
 * so the ATA seed MUST use `TOKEN_PROGRAM_ID` — deriving with Token-2022 yields
 * a different (non-existent) address and the balance reads as 0.
 */
export function deriveOutcomeTokenAta(walletAddress: string, mintAddress: string): PublicKey {
  const wallet = new PublicKey(walletAddress);
  const mint = new PublicKey(mintAddress);
  const [ata] = PublicKey.findProgramAddressSync(
    [wallet.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), mint.toBuffer()],
    ATA_PROGRAM_ID,
  );
  return ata;
}

/**
 * Parse the token balance from raw SPL Token account data.
 *
 * Token account data layout (both SPL Token and Token-2022):
 * - bytes 0-31:  mint pubkey
 * - bytes 32-63: owner pubkey
 * - bytes 64-71: amount (little-endian u64)
 *
 * Returns 0n if data is too short (account doesn't have the expected layout).
 */
export function parseTokenAccountBalance(data: Buffer | Uint8Array): bigint {
  if (data.length < 72) return BigInt(0);

  const amountBytes = data.subarray(64, 72);
  let amount = BigInt(0);
  for (let i = 0; i < 8; i++) {
    const byte = amountBytes[i];
    if (byte !== undefined) {
      amount = amount + BigInt(byte) * (BigInt(1) << BigInt(8 * i));
    }
  }
  return amount;
}
